const _ = require("lodash");

/**
 * Small utility to handle rounding several terms and their sum so that the
 * sum still looks reasonable after rounding. This utility makes the assumption
 * that the sum is unimpeachable and the terms should be fudged instead.
 *
 * We use this for sanitizing times shown on our activity pages, where we load
 * a total time for each day of the month first, and only later load the
 * constituent times for an individual day. This is why the sum can't be
 * altered.
 *
 * The userDailyActivityCleanup cron tries to ensure the sum will at least be
 * feasible. See T47929 for more info on the efforts taken here.
 */

/**
 * If the fractional part of rounding is within this amount of 0 or 1, always
 * round to the nearest number. We do this even if it makes a feasible sum
 * impossible to reach.
 */
const TOLERANCE_FOR_FORCING_NEAREST = 0;

/**
 * Given just the sum, round only it. We use this in the case where the terms
 * aren't loaded yet but we have to display the sum now. In particular, this
 * function and roundToMatchSum must agree.
 *
 * @param {number} sum
 * @param {number} roundingMultiple
 * @returns {number}
 */
const roundSumOnly = function(sum, roundingMultiple = 1) {
	// In these functions, r in front of a variable name means rounded, and s in
	// front of a variable name means scaled (divided by roundingMultiple).
	const sSum = sum / roundingMultiple;
	const floor = Math.floor(sSum);
	const frac = sSum - floor;
	// Always round up unless the tolerance forces otherwise.
	const srSum = floor + (frac <= TOLERANCE_FOR_FORCING_NEAREST ? 0 : 1);
	const rSum = srSum * roundingMultiple;
	return rSum;
};

/**
 * Given a sum and its terms, an array, round all of them so that the terms
 * still add up to the sum, within reason. Returns a {sum, terms} object
 * corresponding to what was passed in. All returned numbers will be a multiple
 * of the passed roundingMultiple.
 *
 * @param {number} sum
 * @param {number[]} terms
 * @param {number} roundingMultiple
 * @returns {object} Object with sum key, a number, and a terms key, an array
 *     of numbers.
 */
const roundToMatchSum = function(sum, terms, roundingMultiple = 1) {
	// Same naming applies as in roundSumOnly.
	const rSum = roundSumOnly(sum, roundingMultiple);
	const srSum = Math.round(rSum / roundingMultiple);

	const n = terms.length;
	const sTerms = terms.map(t => t / roundingMultiple);
	const sFloors = sTerms.map(t => Math.floor(t));
	const sFracs = sTerms.map((t, i) => t - sFloors[i]);
	// Term indices sorted in order of increasing fractional part.
	const indicesByFrac = _.sortBy(_.range(n), i => sFracs[i]);

	// This array is mutated to be our rounding results. What we assign here is
	// just the starting point.
	const srTerms = sFloors.slice(0);
	// We need to increase the srTerms sum by this much.
	const numToRoundUp = srSum - _.sum(srTerms);
	// We try to match the sum by rounding up the terms with the highest
	// fractional parts here, but we respect the forcing tolerance before that.
	// Also, if the sum is infeasible and numToRoundUp is not from 0 to n, then this
	// will at least round the rest of the terms in the right direction.
	indicesByFrac.forEach((ind, i) => {
		if (sFracs[ind] <= TOLERANCE_FOR_FORCING_NEAREST) {
			// Forced to round down
		} else if (sFracs[ind] >= 1 - TOLERANCE_FOR_FORCING_NEAREST) {
			// Forced to round up
			srTerms[ind] += 1;
		} else if (i + numToRoundUp >= n) {
			srTerms[ind] += 1;
		}
	});

	const rTerms = srTerms.map(t => t * roundingMultiple);
	return {
		sum: rSum,
		terms: rTerms,
	};
};

module.exports = {
	roundSumOnly,
	roundToMatchSum,
};
